/*
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  ASSET: Cart Drawer V2 Script
  Purpose: Opens, closes and refreshes the cart drawer, updates quantities, removes products with their free gifts, switches products to and from a subscription, adds upsell products, keeps subscription plans and gifts correct, and keeps the header cart count in sync.
  Last updated: October 2026
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
*/

(() => {
  if (window.elCartDrawerV2) return;

  const DRAWER_SELECTOR = '[data-el-cart-drawer]';
  const BODY_SELECTOR = '[data-el-cart-body]';
  const ROW_SELECTOR = '[data-el-cart-item]';
  const OPEN_TRIGGER_SELECTOR = [
    '[data-el-cart-open]',
    '.el-cart-button',
    'a.action__cart',
    '#header-group a[href$="/cart"]',
    '.shopify-section-group-el-header-group a[href$="/cart"]',
    'header a[href$="/cart"]'
  ].join(', ');
  const COUNT_SELECTOR = '[data-el-cart-count], .el-cart-count';
  const OPEN_CLASS = 'el-cart-drawer-v2-open';
  const VISIBLE_CLASS = 'el-cart-drawer-v2--visible';
  const LOADING_CLASS = 'el-cart-drawer-v2--loading';
  const LOADING_DELAY = 200;
  const LOADING_HIDE_DELAY = 150;
  const SUBSCRIPTION_SETTLE_TIME = 4000;
  const ANIMATION_DURATION = 350;
  const QUANTITY_DELAY = 350;
  const SYNC_DELAY = 200;
  const GIFT_ADD_LIMIT = 2;
  const RATE_LIMIT_PAUSE = 60000;
  const RETURNING_MINIMUM = 9999;
  const INTENT_HOLD = 2500;
  const GIFT_CHECK_DELAY = 2000;
  const GIFT_SYNC_LIMIT = 2500;
  const GIFT_SYNC_INTERVAL = 300;
  const GENERIC_ERROR = 'Sorry, something went wrong. Please try again.';
  const CART_MUTATION_PATTERN = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const OWN_REQUEST_HEADER = 'X-El-Cart-Drawer';
  const APPSTLE_PARENT_PROPERTY = '_appstle_parent_product';
  const APPSTLE_PLAN_PROPERTY = '_appstle_parent_selling_plan';

  let lastTrigger = null;
  let closeTimer = null;
  let latestRequest = 0;
  let queue = Promise.resolve();
  let syncTimer = null;
  let activeCartRequests = 0;
  let rulesRunning = false;
  let rulesQueued = false;
  let removalsInFlight = 0;
  let pendingSubscription = null;
  let actionVersion = 0;
  let rulesPromise = Promise.resolve();
  const pendingChanges = new Map();
  const previewGiftIds = new Set();
  const hiddenKeys = new Set();
  const giftAttempts = new Map();
  const giftRemovals = new Map();
  const intents = new Map();
  let giftCheckTimer = null;
  let rulesPausedUntil = 0;
  let checkoutRunning = false;
  let checkoutReady = false;

  const getDrawer = () => document.querySelector(DRAWER_SELECTOR);

  function getRoot() {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return root || '/';
  }

  function getSectionId() {
    const drawer = getDrawer();
    return drawer ? drawer.dataset.sectionId : null;
  }

  let shownCount = null;
  let queuedCount = null;

  function writeCount(count) {
    shownCount = String(count);
    document.querySelectorAll(COUNT_SELECTOR).forEach((element) => {
      if (element.textContent !== shownCount) element.textContent = shownCount;
    });
  }

  function flushCount() {
    if (queuedCount === null || isMutating()) return;
    writeCount(queuedCount);
    queuedCount = null;
  }

  function updateCount(count) {
    if (count === null || count === undefined) return;
    queuedCount = String(count);
    flushCount();
  }

  const countObserver =
    'MutationObserver' in window
      ? new MutationObserver(() => {
          if (shownCount === null) return;
          let changedOutside = false;
          document.querySelectorAll(COUNT_SELECTOR).forEach((element) => {
            if (element.textContent !== shownCount) {
              element.textContent = shownCount;
              changedOutside = true;
            }
          });
          if (changedOutside && !isMutating()) scheduleSync();
        })
      : null;

  function watchCountElements() {
    if (!countObserver) return;
    const containers = new Set();
    const header = document.querySelector('#header-group, .shopify-section-group-el-header-group, header');
    if (header) containers.add(header);
    document.querySelectorAll('.el-cart-count').forEach((element) => {
      containers.add(element.closest('.shopify-section, header') || element.parentElement || element);
    });
    containers.forEach((container) => {
      countObserver.observe(container, { childList: true, characterData: true, subtree: true });
    });
  }

  const borderObserver =
    'ResizeObserver' in window
      ? new ResizeObserver((entries) => entries.forEach((entry) => drawDashedBorder(entry.target)))
      : null;

  function drawDashedBorder(box) {
    const svg = box.querySelector('.el-cart-subscription__border');
    const path = svg && svg.querySelector('[data-el-cart-dashed-border]');
    if (!path) return;

    const styles = getComputedStyle(box);
    const width = parseFloat(styles.width);
    const height = parseFloat(styles.height);
    if (!width || !height) return;

    const line = parseFloat(styles.getPropertyValue('--el-cart-sub-line')) || 1;
    const radius = parseFloat(styles.getPropertyValue('--el-cart-sub-radius')) || 0;
    const round = (value) => Math.round(value * 1000) / 1000;
    const inset = round(line / 2);
    const arc = round(radius - inset);
    const right = round(width - inset);
    const bottom = round(height - inset);
    const top = round(width - radius);
    const side = round(height - radius);

    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
    path.setAttribute(
      'd',
      [
        `M${inset} ${inset}`,
        `H${top}`,
        `A${arc} ${arc} 0 0 1 ${right} ${radius}`,
        `V${side}`,
        `A${arc} ${arc} 0 0 1 ${top} ${bottom}`,
        `H${radius}`,
        `A${arc} ${arc} 0 0 1 ${inset} ${side}`,
        'Z'
      ].join(' ')
    );
  }

  function watchDashedBorders() {
    const drawer = getDrawer();
    if (!drawer) return;
    if (borderObserver) borderObserver.disconnect();
    drawer.querySelectorAll('.el-cart-subscription__box').forEach((box) => {
      drawDashedBorder(box);
      if (borderObserver) borderObserver.observe(box);
    });
  }

  function parseCart(root) {
    const element = root && root.querySelector('[data-el-cart-json]');
    if (!element) return null;
    try {
      const cart = JSON.parse(element.textContent);
      const plans = root.querySelector('[data-el-cart-plans]');
      cart.elPlans = plans ? JSON.parse(plans.textContent) : {};
      return cart;
    } catch (error) {
      return null;
    }
  }

  function readCart() {
    return parseCart(getDrawer());
  }

  function cartFromMarkup(sectionMarkup) {
    if (!sectionMarkup) return null;
    return parseCart(new DOMParser().parseFromString(sectionMarkup, 'text/html'));
  }

  function findRow(key) {
    const drawer = getDrawer();
    if (!drawer) return null;
    return Array.from(drawer.querySelectorAll(ROW_SELECTOR)).find((row) => row.dataset.lineKey === key) || null;
  }

  function showError(key, message) {
    const row = findRow(key);
    const error = row && row.querySelector('[data-el-cart-error]');
    if (!error) return;
    error.textContent = message || GENERIC_ERROR;
    error.hidden = false;
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  }

  function convertShippingProgress() {
    const drawer = getDrawer();
    const progress = drawer && drawer.querySelector('[data-el-cart-progress]');
    if (!progress) return;
    const baseThreshold = Number(progress.dataset.threshold) || 0;
    const currency = progress.dataset.currency;
    const rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    if (!baseThreshold || !currency || progress.dataset.thresholdLocal === 'true') return;
    if (currency === progress.dataset.shopCurrency || rate === 1) return;

    const threshold = Math.round(baseThreshold * rate);
    const total = Number(progress.dataset.total) || 0;
    const remaining = threshold - total;
    const percent = Math.min(100, Math.floor((total * 100) / threshold));
    const text = progress.querySelector('.el-cart-progress__text');
    const bar = progress.querySelector('[role="progressbar"]');
    const fill = progress.querySelector('.el-cart-progress__fill');

    progress.classList.toggle('el-cart-progress--unlocked', remaining <= 0);
    if (text) {
      text.innerHTML =
        remaining > 0
          ? (progress.dataset.lockedText || '').replace('[price]', formatMoney(remaining, currency))
          : progress.dataset.unlockedText || '';
    }
    if (bar) bar.setAttribute('aria-valuenow', String(percent));
    if (fill) fill.style.width = `${percent}%`;
  }

  function render(sectionMarkup) {
    const drawer = getDrawer();
    if (!drawer || !sectionMarkup) return;

    const markup = new DOMParser().parseFromString(sectionMarkup, 'text/html');
    const nextBody = markup.querySelector(BODY_SELECTOR);
    const currentBody = drawer.querySelector(BODY_SELECTOR);
    if (!nextBody || !currentBody) return;

    const active = document.activeElement;
    const focusKey = active && active.dataset ? active.dataset.lineKey : null;
    const focusAction = active && active.dataset ? active.dataset.elCartAction : null;

    currentBody.innerHTML = nextBody.innerHTML;
    currentBody.dataset.elCartItemCount = nextBody.dataset.elCartItemCount;

    const nextFooter = markup.querySelector('[data-el-cart-footer]');
    const currentFooter = drawer.querySelector('[data-el-cart-footer]');
    if (nextFooter && currentFooter) {
      currentFooter.innerHTML = nextFooter.innerHTML;
      currentFooter.hidden = nextFooter.hidden;
    }
    convertShippingProgress();
    applyPendingRows();
    confirmIntents(markup);
    scheduleGiftCheck();
    if (!intents.size) {
      updateCount((parseInt(nextBody.dataset.elCartItemCount, 10) || 0) + countPreviewRows(currentBody));
    }
    applyIntents();
    applyPendingSubscription();
    watchDashedBorders();

    if (focusKey && focusAction) {
      const target = Array.from(currentBody.querySelectorAll('[data-el-cart-action]')).find(
        (button) => button.dataset.lineKey === focusKey && button.dataset.elCartAction === focusAction
      );
      if (target && !target.disabled) {
        target.focus();
      } else {
        const close = drawer.querySelector('[data-el-cart-close]');
        if (close) close.focus();
      }
    }
  }

  async function postCart(path, body) {
    const sectionId = getSectionId();
    const response = await fetch(`${getRoot()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', [OWN_REQUEST_HEADER]: '1' },
      body: JSON.stringify(Object.assign({}, body, { sections: [sectionId], sections_url: window.location.pathname }))
    });
    const data = await response.json().catch(() => ({}));
    if (response.status === 429) rulesPausedUntil = Date.now() + RATE_LIMIT_PAUSE;
    return { ok: response.ok, data, sectionMarkup: data.sections && data.sections[sectionId] };
  }

  async function changeLines(changes) {
    let last = null;
    let failed = null;
    for (const change of changes) {
      const result = await postCart('cart/change.js', { id: change.key, quantity: change.quantity });
      if (!result.ok && change.quantity === 0 && result.data && result.data.status === 400) continue;
      if (result.ok) {
        last = result;
      } else {
        failed = result;
      }
    }
    return { last, failed };
  }

  function getGiftRules() {
    const drawer = getDrawer();
    const value = drawer ? drawer.dataset.elCartGifts || '' : '';
    const giftsByVariant = new Map();

    value.split(';').forEach((entry) => {
      const [productValue, variantValue, mode, minimumValue] = entry.split(':');
      const productId = Number(productValue);
      const variantId = Number(variantValue);
      if (!productId || !variantId) return;
      if (!giftsByVariant.has(variantId)) {
        giftsByVariant.set(variantId, { productIds: [], appstle: false, minimum: {} });
      }
      const rule = giftsByVariant.get(variantId);
      rule.productIds.push(productId);
      rule.minimum[productId] = Number(minimumValue) || 1;
      if (mode === 'a') rule.appstle = true;
    });

    return giftsByVariant;
  }

  function qualifiesForGift(rule, item, giftVariantIds) {
    return Boolean(
      !giftVariantIds.has(item.variant_id) &&
        !getParentVariant(item) &&
        item.selling_plan_allocation &&
        rule.productIds.includes(item.product_id) &&
        item.quantity >= (rule.minimum[item.product_id] || 1)
    );
  }

  function getParentVariant(item) {
    const parent = item.properties && item.properties[APPSTLE_PARENT_PROPERTY];
    return parent ? String(parent) : null;
  }

  function getPackPlan(plans, quantity, slot) {
    let group = 0;
    if (quantity === 1) group = 1;
    if (quantity >= 3 && plans.length >= 9) group = 2;
    return plans[group * 3 + slot];
  }

  function getPlanFixes(cart) {
    const fixes = [];
    const planMap = cart.elPlans || {};
    cart.items.forEach((item) => {
      const plans = (planMap[item.variant_id] || []).map(String);
      const currentPlan = item.selling_plan_allocation && String(item.selling_plan_allocation.selling_plan.id);
      if (!currentPlan || plans.length < 6) return;

      const index = plans.indexOf(currentPlan);
      if (index < 0) return;

      const correctPlan = getPackPlan(plans, item.quantity, index % 3);
      if (correctPlan && correctPlan !== currentPlan) {
        fixes.push({ id: item.key, quantity: item.quantity, selling_plan: Number(correctPlan) });
      }
    });
    return fixes;
  }

  function noteGiftAttempt(attempt) {
    if (!giftAttempts.has(attempt.giftId)) giftAttempts.set(attempt.giftId, new Map());
    const counts = giftAttempts.get(attempt.giftId);
    counts.set(attempt.signature, (counts.get(attempt.signature) || 0) + 1);
  }

  function noteGiftRemoval(signature) {
    giftRemovals.set(signature, (giftRemovals.get(signature) || 0) + 1);
  }

  function appstleOnPage() {
    return Boolean(window.AppstleSubscription || window.RSConfig || window._RSConfig);
  }

  function getAppstleGiftChanges(items, giftLines, giftVariantIds, remove, removals, fix) {
    const parents = items.filter(
      (item) => item.selling_plan_allocation && !getParentVariant(item) && !giftVariantIds.has(item.variant_id)
    );
    const parentOf = (line) => parents.find((item) => String(item.variant_id) === getParentVariant(line));
    const planOf = (line) => String((line.properties && line.properties[APPSTLE_PLAN_PROPERTY]) || '');
    const current = giftLines.find((line) => {
      const parent = parentOf(line);
      return parent && String(parent.selling_plan_allocation.selling_plan.id) === planOf(line);
    });

    giftLines.forEach((line) => {
      if (!getParentVariant(line)) return;
      if (parentOf(line) && (!current || line === current)) {
        if (line.quantity > 1) fix.push(line.key);
        return;
      }
      const signature = `${line.variant_id}|${getParentVariant(line)}|${planOf(line)}`;
      if ((giftRemovals.get(signature) || 0) >= GIFT_ADD_LIMIT) return;
      remove.push(line.key);
      removals.push(signature);
    });
  }

  function getGiftChanges(items) {
    const giftRules = getGiftRules();
    const giftVariantIds = new Set(giftRules.keys());
    const add = [];
    const remove = [];
    const fix = [];
    const attempts = [];
    const removals = [];

    giftRules.forEach((rule, giftVariantId) => {
      const giftLines = items.filter((item) => item.variant_id === giftVariantId);
      if (rule.appstle && appstleOnPage()) {
        getAppstleGiftChanges(items, giftLines, giftVariantIds, remove, removals, fix);
        return;
      }
      const parentLines = items.filter((item) => qualifiesForGift(rule, item, giftVariantIds));
      const parentLine = parentLines[0];
      const keptByAppstle = giftLines.some((line) => {
        const parent = getParentVariant(line);
        return parent && parentLines.some((item) => String(item.variant_id) === parent);
      });

      if (!parentLine && !keptByAppstle) {
        giftAttempts.delete(giftVariantId);
        giftLines.forEach((line) => remove.push(line.key));
        return;
      }

      if (!giftLines.length) {
        if (!parentLine) return;
        const signature = `${parentLine.variant_id}|${parentLine.selling_plan_allocation.selling_plan.id}`;
        const counts = giftAttempts.get(giftVariantId);
        if (counts && (counts.get(signature) || 0) >= GIFT_ADD_LIMIT) return;
        attempts.push({ giftId: giftVariantId, signature });
        const gift = { id: giftVariantId, quantity: 1 };
        if (rule.appstle && parentLine) {
          gift.properties = {
            [APPSTLE_PARENT_PROPERTY]: parentLine.variant_id,
            [APPSTLE_PLAN_PROPERTY]: parentLine.selling_plan_allocation.selling_plan.id
          };
        }
        add.push(gift);
        return;
      }

      const matchesParent = (line) => {
        const parent = getParentVariant(line);
        const plan = line.properties && line.properties[APPSTLE_PLAN_PROPERTY];
        return parentLines.some(
          (item) =>
            String(item.variant_id) === parent && String(item.selling_plan_allocation.selling_plan.id) === String(plan)
        );
      };
      const keptLine = giftLines.find(matchesParent) || giftLines[0];
      giftLines.forEach((line) => {
        if (line !== keptLine) {
          remove.push(line.key);
          return;
        }
        if (line.quantity > 1) fix.push(line.key);
      });
    });

    return { add, remove, fix, attempts, removals };
  }

  function showGiftPreviews(variantIds) {
    const drawer = getDrawer();
    const list = drawer && drawer.querySelector('.el-cart-items');
    if (!list) return;

    const cart = readCart();
    const inCart = new Set(cart ? cart.items.map((item) => item.variant_id) : []);

    variantIds.forEach((variantId) => {
      if (inCart.has(variantId) || findRow(`preview-${variantId}`)) return;
      const template = drawer.querySelector(`template[data-el-gift-template="${variantId}"]`);
      if (!template) return;
      const order = Number(template.dataset.elGiftOrder) || 0;
      const before = Array.from(list.children).find(
        (row) => row.dataset.elGiftOrder !== undefined && Number(row.dataset.elGiftOrder) > order
      );
      list.insertBefore(template.content.cloneNode(true), before || null);
    });
  }

  function hideRows(keys) {
    keys.forEach((key) => {
      const row = findRow(key);
      if (row) row.hidden = true;
    });
  }

  function applyPendingRows() {
    hideRows(Array.from(hiddenKeys));
    showGiftPreviews(Array.from(previewGiftIds));
    const body = getDrawer() && getDrawer().querySelector(BODY_SELECTOR);
    if (intents.size) {
      applyIntents();
      return;
    }
    const previewCount = countPreviewRows(body);
    if (previewCount) writeCount((parseInt(body.dataset.elCartItemCount, 10) || 0) + previewCount);
  }

  function countPreviewRows(body) {
    if (!body) return 0;
    return body.querySelectorAll('[data-line-key^="preview-"]:not([data-el-gift-server]):not([hidden])').length;
  }

  async function runCartRules(nextMarkup) {
    if (!getDrawer()) return;
    let waitingMarkup = nextMarkup || null;
    const showWaitingMarkup = () => {
      if (!waitingMarkup) return;
      latestRequest += 1;
      render(waitingMarkup);
      waitingMarkup = null;
    };

    if (Date.now() < rulesPausedUntil) {
      showWaitingMarkup();
      return;
    }
    if (rulesRunning || removalsInFlight) {
      showWaitingMarkup();
      rulesQueued = true;
      return;
    }
    const savedCart = waitingMarkup ? cartFromMarkup(waitingMarkup) : readCart();
    if (!savedCart) {
      showWaitingMarkup();
      return;
    }

    const cart = Object.assign({}, savedCart, {
      items: savedCart.items.filter((item) => !hiddenKeys.has(item.key))
    });
    const planFixes = getPlanFixes(cart);
    const giftChanges = getGiftChanges(cart.items);
    if (!planFixes.length) showWaitingMarkup();
    if (!planFixes.length && !giftChanges.add.length && !giftChanges.remove.length && !giftChanges.fix.length) return;

    const addedIds = giftChanges.add.map((gift) => gift.id);
    const version = actionVersion;
    const stillCurrent = () => version === actionVersion && !pendingChanges.size;
    let finishRules = null;
    rulesPromise = new Promise((resolve) => {
      finishRules = resolve;
    });
    rulesRunning = true;
    addedIds.forEach((id) => previewGiftIds.add(id));
    giftChanges.remove.forEach((key) => hiddenKeys.add(key));
    applyPendingRows();

    try {
      let lastMarkup = null;

      for (const fix of planFixes) {
        if (!stillCurrent()) break;
        const result = await postCart('cart/change.js', fix);
        if (result.sectionMarkup) lastMarkup = result.sectionMarkup;
      }

      if (giftChanges.add.length && stillCurrent()) {
        giftChanges.attempts.forEach(noteGiftAttempt);
        const result = await postCart('cart/add.js', { items: giftChanges.add });
        if (result.sectionMarkup) lastMarkup = result.sectionMarkup;
        if (!result.ok) {
          addedIds.forEach((id) => previewGiftIds.delete(id));
          lastMarkup = null;
        }
      }

      if ((giftChanges.remove.length || giftChanges.fix.length) && stillCurrent()) {
        giftChanges.removals.forEach(noteGiftRemoval);
        const changes = giftChanges.remove
          .map((key) => ({ key, quantity: 0 }))
          .concat(giftChanges.fix.map((key) => ({ key, quantity: 1 })));
        const result = await changeLines(changes);
        if (result.last && result.last.sectionMarkup) lastMarkup = result.last.sectionMarkup;
        if (result.failed) lastMarkup = null;
      }

      if (stillCurrent()) {
        latestRequest += 1;
        if (lastMarkup) {
          render(lastMarkup);
        } else {
          await refresh(true);
        }
      }
    } catch (error) {
      console.error('Cart drawer could not apply cart rules', error);
    } finally {
      rulesRunning = false;
      if (finishRules) finishRules();
      setTimeout(flushCount, 0);
      addedIds.forEach((id) => previewGiftIds.delete(id));
      giftChanges.remove.forEach((key) => hiddenKeys.delete(key));
      if (rulesQueued) {
        rulesQueued = false;
        if (stillCurrent()) runCartRules();
      }
    }
  }

  function isMutating() {
    return busyCount > 0 || rulesRunning || removalsInFlight > 0 || pendingChanges.size > 0;
  }

  async function refresh(force) {
    const drawer = getDrawer();
    if (!drawer) return;

    const sectionId = drawer.dataset.sectionId;
    const requestId = ++latestRequest;

    try {
      const response = await fetch(`${getRoot()}?sections=${encodeURIComponent(sectionId)}`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      const sections = await response.json();
      if (requestId !== latestRequest) return;
      if (force !== true && isMutating()) return;
      render(sections[sectionId]);
    } catch (error) {
      console.error('Cart drawer could not refresh', error);
    }
  }

  async function syncCart() {
    await refresh();
    runCartRules();
  }

  function getChangeBody(key, quantity) {
    const body = { id: key, quantity };
    const cart = readCart();
    const line = cart && cart.items.find((item) => item.key === key);
    if (!line || !line.selling_plan_allocation) return body;
    const plans = ((cart.elPlans || {})[line.variant_id] || []).map(String);
    const currentPlan = String(line.selling_plan_allocation.selling_plan.id);
    const index = plans.indexOf(currentPlan);
    if (plans.length < 6 || index < 0) return body;
    const correctPlan = getPackPlan(plans, quantity, index % 3);
    if (correctPlan && correctPlan !== currentPlan) body.selling_plan = Number(correctPlan);
    return body;
  }

  function scheduleGiftCheck() {
    clearTimeout(giftCheckTimer);
    giftCheckTimer = setTimeout(runGiftCheck, GIFT_CHECK_DELAY);
  }

  async function runGiftCheck() {
    giftCheckTimer = null;
    if (!appstleOnPage() || Date.now() < rulesPausedUntil) return;
    if (isMutating() || activeCartRequests > 0 || syncTimer) {
      scheduleGiftCheck();
      return;
    }
    const cart = readCart();
    if (!cart) return;
    const repairs = getAppstleRepairs(cart);
    if (!repairs.add.length && !repairs.remove.length) return;
    const markup = await applyAppstleRepairs(repairs);
    if (markup && !isMutating()) {
      latestRequest += 1;
      render(markup);
    }
  }

  async function sendChange(key, quantity) {
    latestRequest += 1;

    try {
      const result = await postCart('cart/change.js', getChangeBody(key, quantity));

      if (!result.ok) {
        await refresh(true);
        showError(key, result.data.description || result.data.message);
        return;
      }

      latestRequest += 1;
      if (pendingChanges.size) return;
      await runCartRules(result.sectionMarkup);
      await waitForAppstleGifts();
    } catch (error) {
      console.error('Cart drawer could not update the cart', error);
      await refresh(true);
      showError(key, GENERIC_ERROR);
    }
  }

  async function sendRemoval(keys) {
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      keys.forEach((key) => hiddenKeys.delete(key));
      removalsInFlight = Math.max(0, removalsInFlight - 1);
    };
    const mainKey = keys[0];
    const savedCart = readCart();
    const mainItem = savedCart && savedCart.items.find((item) => item.key === mainKey);

    try {
      await waitForCartIdle();
      let result = await postCart('cart/update.js', { updates: toUpdates(keys) });

      if (!result.ok) {
        const freshCart = await fetchCart();
        const freshMain =
          freshCart &&
          (freshCart.items.find((item) => item.key === mainKey) ||
            (mainItem && freshCart.items.find((item) => item.variant_id === mainItem.variant_id && !getParentVariant(item))));
        if (freshMain) {
          const freshKeys = [freshMain.key].concat(getDependentKeys(freshMain.key, freshCart));
          result = await postCart('cart/update.js', { updates: toUpdates(freshKeys) });
          if (!result.ok) {
            const fallback = await changeLines(freshKeys.map((key) => ({ key, quantity: 0 })));
            if (fallback.last && !fallback.failed) result = fallback.last;
          }
        } else if (freshCart) {
          result = { ok: true, data: {}, sectionMarkup: null };
        }
      }

      if (!result.ok) {
        release();
        await refresh(true);
        showError(mainKey, result.data.description || result.data.message);
        return;
      }

      release();
      latestRequest += 1;
      if (pendingChanges.size) return;
      if (result.sectionMarkup) {
        render(result.sectionMarkup);
      } else {
        await refresh(true);
      }
      await runCartRules();
    } catch (error) {
      console.error('Cart drawer could not remove the product', error);
      release();
      await refresh(true);
    }
  }

  function toUpdates(keys) {
    const updates = {};
    keys.forEach((key) => {
      updates[key] = 0;
    });
    return updates;
  }

  async function fetchCart() {
    try {
      const response = await fetch(`${getRoot()}cart.js`, {
        headers: { Accept: 'application/json', [OWN_REQUEST_HEADER]: '1' }
      });
      return response.ok ? await response.json() : null;
    } catch (error) {
      return null;
    }
  }

  function parseSubscriptionLines(value) {
    return (value || '')
      .split(';')
      .filter(Boolean)
      .map((entry) => {
        const [key, quantity, plan, state] = entry.split('|');
        return { key, quantity: Number(quantity), plan: Number(plan), subscribed: state === '1' };
      });
  }

  function applyPendingSubscription() {
    if (!pendingSubscription) return;
    if (Date.now() > pendingSubscription.expires) {
      pendingSubscription = null;
      return;
    }

    const drawer = getDrawer();
    const button =
      drawer && drawer.querySelector(`[data-el-cart-subscription][data-variant-id="${pendingSubscription.variantId}"]`);
    if (!button) return;

    button.setAttribute('aria-checked', String(pendingSubscription.checked));
    const box = button.closest('[data-el-cart-subscription-box]');
    if (box) box.classList.toggle('el-cart-subscription--active', pendingSubscription.checked);
  }

  async function sendSubscriptionChange(changes, variantId) {
    let last = null;
    let failed = null;

    try {
      await waitForCartIdle();
      for (const change of changes) {
        let result = await postCart('cart/change.js', change);
        if (!result.ok) {
          const freshCart = await fetchCart();
          const line =
            freshCart &&
            (freshCart.items.find((item) => item.key === change.id) ||
              freshCart.items.find((item) => String(item.variant_id) === String(variantId) && !getParentVariant(item)));
          if (line) {
            const alreadyDone = change.selling_plan === null ? !line.selling_plan_allocation : Boolean(line.selling_plan_allocation);
            result = alreadyDone
              ? { ok: true, data: {}, sectionMarkup: null }
              : await postCart('cart/change.js', Object.assign({}, change, { id: line.key, quantity: line.quantity }));
          }
        }
        if (result.ok) {
          last = result;
        } else {
          failed = result;
        }
      }
    } catch (error) {
      console.error('Cart drawer could not update the subscription', error);
    }

    removalsInFlight = Math.max(0, removalsInFlight - 1);
    latestRequest += 1;

    if (failed || !last) {
      pendingSubscription = null;
    } else if (pendingSubscription) {
      pendingSubscription.expires = Date.now() + SUBSCRIPTION_SETTLE_TIME;
    }

    if (last && last.sectionMarkup && !failed && !pendingChanges.size) {
      await runCartRules(last.sectionMarkup);
      await waitForAppstleGifts();
      return;
    }

    if (last && last.sectionMarkup && !pendingChanges.size) {
      render(last.sectionMarkup);
    } else {
      await refresh(true);
    }

    if (failed || !last) {
      const drawer = getDrawer();
      const box = drawer && drawer.querySelector(`[data-el-cart-subscription-box][data-variant-id="${variantId}"]`);
      const error = box && box.querySelector('[data-el-cart-subscription-error]');
      if (error) {
        error.textContent = (failed && failed.data && (failed.data.description || failed.data.message)) || GENERIC_ERROR;
        error.hidden = false;
      }
    }

    await runCartRules();
  }

  function toggleSubscription(button) {
    if (button.getAttribute('aria-busy') === 'true') return;

    const turnOn = button.getAttribute('aria-checked') !== 'true';
    const changes = parseSubscriptionLines(button.dataset.lines)
      .filter((line) => (turnOn ? !line.subscribed : line.subscribed))
      .map((line) => ({ id: line.key, quantity: line.quantity, selling_plan: turnOn ? line.plan : null }));
    if (!changes.length) return;

    const box = button.closest('[data-el-cart-subscription-box]');
    button.setAttribute('aria-checked', String(turnOn));
    button.setAttribute('aria-busy', 'true');
    if (box) box.classList.toggle('el-cart-subscription--active', turnOn);

    const variantId = button.dataset.variantId;
    actionVersion += 1;
    pendingSubscription = { variantId, checked: turnOn, expires: Date.now() + 30000 };
    removalsInFlight += 1;
    enqueue(() => sendSubscriptionChange(changes, variantId));
  }

  function addUpsell(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    pendingSubscription = null;
    actionVersion += 1;

    const id = Number(button.dataset.variantId);
    const error = button.parentElement && button.parentElement.querySelector('[data-el-cart-upsell-error]');
    if (error) error.hidden = true;
    button.setAttribute('aria-busy', 'true');

    enqueue(async () => {
      try {
        const result = await postCart('cart/add.js', { items: [{ id, quantity: 1 }] });

        if (!result.ok) {
          button.removeAttribute('aria-busy');
          if (error) {
            error.textContent = result.data.description || result.data.message || GENERIC_ERROR;
            error.hidden = false;
          }
          return;
        }

        latestRequest += 1;
        if (pendingChanges.size) return;
        render(result.sectionMarkup);
        await runCartRules();
      } catch (requestError) {
        console.error('Cart drawer could not add the product', requestError);
        button.removeAttribute('aria-busy');
        if (error) {
          error.textContent = GENERIC_ERROR;
          error.hidden = false;
        }
      }
    });
  }

  function getUpsellParts(element) {
    const upsell = element.closest('[data-el-cart-upsell]');
    if (!upsell) return null;
    const track = upsell.querySelector('[data-el-cart-upsell-track]');
    const slides = Array.from(upsell.querySelectorAll('[data-el-cart-upsell-slide]'));
    const dots = Array.from(upsell.querySelectorAll('[data-el-cart-upsell-dot]'));
    return track && slides.length ? { track, slides, dots } : null;
  }

  function updateUpsellDots(track) {
    const parts = getUpsellParts(track);
    if (!parts || !parts.dots.length) return;

    const { slides, dots } = parts;
    const step = slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : 1;
    let index = Math.round(track.scrollLeft / step);
    if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 2) index = slides.length - 1;

    dots.forEach((dot, dotIndex) => {
      dot.classList.toggle('el-cart-upsell__dot--active', dotIndex === index);
    });
  }

  function goToUpsell(dot) {
    const parts = getUpsellParts(dot);
    if (!parts) return;

    const slide = parts.slides[Number(dot.dataset.elCartUpsellDot)];
    if (!slide) return;

    parts.track.scrollTo({
      left: slide.offsetLeft - parts.slides[0].offsetLeft,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth'
    });
  }

  let busyCount = 0;
  let busyTimer = null;
  let hideTimer = null;

  function setBusy(change) {
    busyCount = Math.max(0, busyCount + change);
    const drawer = getDrawer();
    if (!drawer) return;

    if (busyCount > 0) {
      clearTimeout(hideTimer);
      hideTimer = null;
      drawer.setAttribute('aria-busy', 'true');
      if (!busyTimer && !drawer.classList.contains(LOADING_CLASS)) {
        busyTimer = setTimeout(() => {
          busyTimer = null;
          const current = getDrawer();
          if (busyCount > 0 && current) current.classList.add(LOADING_CLASS);
        }, LOADING_DELAY);
      }
      return;
    }

    clearTimeout(busyTimer);
    busyTimer = null;
    clearTimeout(hideTimer);
    hideTimer = setTimeout(() => {
      hideTimer = null;
      if (busyCount > 0) return;
      flushCount();
      const current = getDrawer();
      if (!current) return;
      current.removeAttribute('aria-busy');
      current.classList.remove(LOADING_CLASS);
    }, LOADING_HIDE_DELAY);
  }

  function enqueue(task) {
    setBusy(1);
    const run = () =>
      Promise.resolve()
        .then(() => rulesPromise)
        .then(task)
        .finally(() => setBusy(-1));
    queue = queue.then(run, run);
    return queue;
  }

  function getDependentKeys(key, sourceCart) {
    const cart = sourceCart || readCart();
    if (!cart) return [];

    const removed = cart.items.find((item) => item.key === key);
    if (!removed) return [];

    const others = cart.items.filter((item) => item.key !== key);
    const giftRules = getGiftRules();
    const giftVariantIds = new Set(giftRules.keys());
    const stillNeeded = (variantId) => {
      const rule = giftRules.get(variantId);
      return Boolean(rule && others.some((item) => qualifiesForGift(rule, item, giftVariantIds)));
    };

    const appstleGifts = others
      .filter((item) => getParentVariant(item) === String(removed.variant_id) && !stillNeeded(item.variant_id))
      .map((item) => item.key);

    const remaining = others.filter((item) => !appstleGifts.includes(item.key));
    const ownGifts = getGiftChanges(remaining).remove;

    return appstleGifts.concat(ownGifts);
  }

  function confirmIntents(markup) {
    const cart = parseCart(markup);
    if (!cart || !intents.size) return;
    intents.forEach((intent, variantId) => {
      const line = cart.items.find((item) => item.variant_id === variantId && !getParentVariant(item));
      if (intent.confirmed || !line || line.quantity !== intent.quantity) return;
      if (Array.from(pendingChanges.keys()).some((key) => cart.items.some((item) => item.key === key && item.variant_id === variantId))) return;
      intent.confirmed = true;
      setTimeout(() => {
        if (intents.get(variantId) === intent) intents.delete(variantId);
      }, INTENT_HOLD);
    });
  }

  function countVisibleItems(body) {
    return Array.from(body.querySelectorAll(ROW_SELECTOR))
      .filter((row) => !row.hidden)
      .reduce((total, row) => {
        const value = row.querySelector('[data-el-cart-quantity]');
        return total + (value ? parseInt(value.textContent, 10) || 0 : 1);
      }, 0);
  }

  function applyIntents() {
    const drawer = getDrawer();
    const body = drawer && drawer.querySelector(BODY_SELECTOR);
    const cart = readCart();
    if (!body || !cart) return;
    let changed = false;

    const items = cart.items
      .filter((item) => !hiddenKeys.has(item.key))
      .map((item) =>
        intents.has(item.variant_id) && !getParentVariant(item)
          ? Object.assign({}, item, { quantity: intents.get(item.variant_id).quantity })
          : item
      );
    const giftRules = getGiftRules();
    const giftVariantIds = new Set(giftRules.keys());

    giftRules.forEach((rule, giftVariantId) => {
      if (Object.values(rule.minimum).some((minimum) => minimum >= RETURNING_MINIMUM)) return;
      const lineKeys = new Set(
        cart.items.filter((item) => item.variant_id === giftVariantId).map((item) => item.key)
      );
      lineKeys.add(`preview-${giftVariantId}`);
      const rows = Array.from(body.querySelectorAll(ROW_SELECTOR)).filter((row) => lineKeys.has(row.dataset.lineKey));
      const due = items.some((item) => qualifiesForGift(rule, item, giftVariantIds));

      if (!due) {
        rows.forEach((row) => {
          if (row.hidden) return;
          row.hidden = true;
          changed = true;
        });
        return;
      }
      if (rows.some((row) => !row.hidden)) return;
      changed = true;
      const hiddenByIntent = rows.find((row) => !hiddenKeys.has(row.dataset.lineKey));
      if (hiddenByIntent) {
        hiddenByIntent.hidden = false;
      } else {
        showGiftPreviews([giftVariantId]);
      }
    });

    if (!intents.size && !changed) return;
    queuedCount = null;
    writeCount(countVisibleItems(body));
  }

  function getAppstleRepairs(cart) {
    const giftRules = getGiftRules();
    const giftVariantIds = new Set(giftRules.keys());
    const add = [];
    const remove = [];
    const attempts = [];
    const removals = [];
    giftRules.forEach((rule, giftVariantId) => {
      if (Object.values(rule.minimum).some((minimum) => minimum >= RETURNING_MINIMUM)) return;
      const parents = cart.items.filter((item) => qualifiesForGift(rule, item, giftVariantIds));
      if (!rule.appstle) {
        if (!parents.length || cart.items.some((item) => item.variant_id === giftVariantId)) return;
        const signature = `${parents[0].variant_id}|${parents[0].selling_plan_allocation.selling_plan.id}`;
        const counts = giftAttempts.get(giftVariantId);
        if (counts && (counts.get(signature) || 0) >= GIFT_ADD_LIMIT) return;
        attempts.push({ giftId: giftVariantId, signature });
        add.push({ id: giftVariantId, quantity: 1 });
        return;
      }
      const lines = cart.items.filter((item) => item.variant_id === giftVariantId && getParentVariant(item));
      const planOf = (line) => String(line.properties[APPSTLE_PLAN_PROPERTY] || '');
      const current = lines.find((line) =>
        parents.some(
          (item) =>
            String(item.variant_id) === getParentVariant(line) &&
            String(item.selling_plan_allocation.selling_plan.id) === planOf(line)
        )
      );
      lines.forEach((line) => {
        if (line === current) return;
        const signature = `${line.variant_id}|${getParentVariant(line)}|${planOf(line)}`;
        if ((giftRemovals.get(signature) || 0) >= GIFT_ADD_LIMIT) return;
        remove.push(line.key);
        removals.push(signature);
      });
      if (!parents.length || current) return;
      const parentLine = parents[0];
      const planId = parentLine.selling_plan_allocation.selling_plan.id;
      const signature = `${parentLine.variant_id}|${planId}`;
      const counts = giftAttempts.get(giftVariantId);
      if (counts && (counts.get(signature) || 0) >= GIFT_ADD_LIMIT) return;
      attempts.push({ giftId: giftVariantId, signature });
      add.push({
        id: giftVariantId,
        quantity: 1,
        properties: { [APPSTLE_PARENT_PROPERTY]: parentLine.variant_id, [APPSTLE_PLAN_PROPERTY]: planId }
      });
    });
    return { add, remove, attempts, removals };
  }

  async function applyAppstleRepairs(repairs) {
    let markup = null;
    if (repairs.add.length) {
      repairs.attempts.forEach(noteGiftAttempt);
      const result = await postCart('cart/add.js', { items: repairs.add });
      if (result.ok && result.sectionMarkup) markup = result.sectionMarkup;
    }
    if (repairs.remove.length) {
      repairs.removals.forEach(noteGiftRemoval);
      const result = await changeLines(repairs.remove.map((key) => ({ key, quantity: 0 })));
      if (result.last && result.last.sectionMarkup) markup = result.last.sectionMarkup;
    }
    return markup;
  }

  async function waitForAppstleGifts() {
    if (!appstleOnPage()) return;
    const started = Date.now();
    let polls = 0;
    let settled = false;
    let lastCart = null;
    while (Date.now() - started < GIFT_SYNC_LIMIT) {
      if (pendingChanges.size) return;
      const cart = await fetchCart();
      if (!cart) return;
      polls += 1;
      lastCart = cart;
      const repairs = getAppstleRepairs(cart);
      if (!repairs.add.length && !repairs.remove.length) {
        settled = true;
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, GIFT_SYNC_INTERVAL));
    }
    if (pendingChanges.size) return;
    if (!settled && lastCart) {
      const markup = await applyAppstleRepairs(getAppstleRepairs(lastCart));
      if (markup && !pendingChanges.size) {
        latestRequest += 1;
        render(markup);
        return;
      }
    }
    if (polls > 1) await refresh(true);
  }

  function getCheckoutFixes(cart) {
    const giftRules = getGiftRules();
    const giftVariantIds = new Set(giftRules.keys());
    const add = [];
    const remove = [];
    giftRules.forEach((rule, giftVariantId) => {
      if (Object.values(rule.minimum).some((minimum) => minimum >= RETURNING_MINIMUM)) return;
      const parent = cart.items.find((item) => qualifiesForGift(rule, item, giftVariantIds));
      const lines = cart.items.filter((item) => item.variant_id === giftVariantId);
      if (!parent) {
        lines.forEach((line) => remove.push(line.key));
        return;
      }
      if (lines.length) return;
      const gift = { id: giftVariantId, quantity: 1 };
      if (rule.appstle) {
        gift.properties = {
          [APPSTLE_PARENT_PROPERTY]: parent.variant_id,
          [APPSTLE_PLAN_PROPERTY]: parent.selling_plan_allocation.selling_plan.id
        };
      }
      add.push(gift);
    });
    return { add, remove };
  }

  async function prepareCheckout() {
    const started = Date.now();
    const busy = () => pendingChanges.size || rulesRunning || removalsInFlight > 0 || activeCartRequests > 0;
    while (busy() && Date.now() - started < 6000) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    await queue.catch(() => {});
    for (let round = 0; round < 3; round += 1) {
      const cart = await fetchCart();
      if (!cart) return;
      const fixes = getCheckoutFixes(cart);
      if (!fixes.add.length && !fixes.remove.length) return;
      if (round === 0) {
        await new Promise((resolve) => setTimeout(resolve, 600));
        continue;
      }
      if (fixes.add.length) await postCart('cart/add.js', { items: fixes.add });
      if (fixes.remove.length) await changeLines(fixes.remove.map((key) => ({ key, quantity: 0 })));
    }
  }

  function startCheckout(form, button) {
    if (checkoutRunning) return;
    checkoutRunning = true;
    setBusy(1);
    if (button) button.setAttribute('aria-busy', 'true');
    prepareCheckout()
      .catch((error) => console.error('Cart drawer could not check the gifts before checkout', error))
      .finally(() => {
        setBusy(-1);
        checkoutRunning = false;
        checkoutReady = true;
        if (button) button.removeAttribute('aria-busy');
        const drawer = getDrawer();
        const currentForm = (form.isConnected ? form : drawer && drawer.querySelector('.el-cart-footer__form')) || form;
        const currentButton = currentForm.querySelector('[name="checkout"]');
        if (!currentForm.isConnected) document.body.appendChild(currentForm);
        if (currentForm.requestSubmit && currentButton) {
          currentForm.requestSubmit(currentButton);
          return;
        }
        const field = document.createElement('input');
        field.type = 'hidden';
        field.name = 'checkout';
        currentForm.appendChild(field);
        currentForm.submit();
      });
  }

  function scheduleChange(key, quantity, delay) {
    pendingSubscription = null;
    actionVersion += 1;
    const intentCart = readCart();
    const intentLine = intentCart && intentCart.items.find((item) => item.key === key);
    if (intentLine) intents.set(intentLine.variant_id, { quantity, confirmed: false });
    const row = findRow(key);
    if (row) {
      row.setAttribute('aria-busy', 'true');
      const value = row.querySelector('[data-el-cart-quantity]');
      if (value) value.textContent = quantity;
      const decrease = row.querySelector('[data-el-cart-action="decrease"]');
      if (decrease) decrease.disabled = quantity <= 1;
      const error = row.querySelector('[data-el-cart-error]');
      if (error) error.hidden = true;
    }

    const existing = pendingChanges.get(key);
    if (existing) clearTimeout(existing.timer);

    const timer = setTimeout(() => {
      pendingChanges.delete(key);
      enqueue(() => sendChange(key, quantity));
    }, delay);

    pendingChanges.set(key, { quantity, timer });
    applyIntents();
  }

  function removeLine(key) {
    pendingSubscription = null;
    actionVersion += 1;
    const removedCart = readCart();
    const removedLine = removedCart && removedCart.items.find((item) => item.key === key);
    if (removedLine) intents.delete(removedLine.variant_id);
    const existing = pendingChanges.get(key);
    if (existing) {
      clearTimeout(existing.timer);
      pendingChanges.delete(key);
    }

    const keys = [key].concat(getDependentKeys(key));
    const cart = readCart();
    const body = getDrawer() && getDrawer().querySelector(BODY_SELECTOR);
    if (cart && body) {
      const remaining = cart.items.filter((item) => !keys.includes(item.key));
      const giftRules = getGiftRules();
      const giftVariantIds = new Set(giftRules.keys());
      const duePreviews = Array.from(body.querySelectorAll('[data-el-gift-server]:not([hidden])')).filter((row) => {
        const rule = giftRules.get(Number(row.dataset.lineKey.replace('preview-', '')));
        return !rule || !remaining.some((item) => qualifiesForGift(rule, item, giftVariantIds));
      });
      duePreviews.forEach((row) => {
        row.hidden = true;
      });
      const removedCount = cart.items
        .filter((item) => keys.includes(item.key) && findRow(item.key) && !findRow(item.key).hidden)
        .reduce((total, item) => total + item.quantity, duePreviews.length);
      writeCount(Math.max(0, (parseInt(body.dataset.elCartItemCount, 10) || 0) - removedCount));
    }
    keys.forEach((item) => hiddenKeys.add(item));
    hideRows(keys);
    removalsInFlight += 1;
    enqueue(() => sendRemoval(keys));
  }

  function handleAction(button) {
    const key = button.dataset.lineKey;
    const action = button.dataset.elCartAction;
    const row = findRow(key);
    if (!key || !row) return;

    if (action === 'remove') {
      removeLine(key);
      return;
    }

    const pending = pendingChanges.get(key);
    const valueElement = row.querySelector('[data-el-cart-quantity]');
    const current = pending ? pending.quantity : parseInt(valueElement ? valueElement.textContent : '0', 10) || 0;

    if (action === 'increase') scheduleChange(key, current + 1, QUANTITY_DELAY);
    if (action === 'decrease' && current > 1) scheduleChange(key, current - 1, QUANTITY_DELAY);
  }

  function waitForCartIdle(timeout = 4000) {
    const started = Date.now();
    return new Promise((resolve) => {
      const check = () => {
        if (activeCartRequests === 0 || Date.now() - started > timeout) {
          resolve();
          return;
        }
        setTimeout(check, 50);
      };
      setTimeout(check, 0);
    });
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function matchPageZoom(drawer) {
    const zoom = parseFloat(getComputedStyle(document.body).zoom) || 1;
    drawer.style.zoom = zoom === 1 ? '' : String(Math.round((1 / zoom) * 10000) / 10000);
  }

  function show(drawer, trigger) {
    clearTimeout(closeTimer);
    closeTimer = null;
    matchPageZoom(drawer);

    if (!drawer.open) {
      lastTrigger = trigger || document.activeElement;
      drawer.showModal();
      document.documentElement.classList.add(OPEN_CLASS);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => drawer.classList.add(VISIBLE_CLASS));
    });
  }

  function open(trigger, options = {}) {
    const drawer = getDrawer();
    if (!drawer) return Promise.resolve();

    if (drawer.open && !closeTimer) return syncCart();

    if (options.waitForContent) {
      return waitForCartIdle()
        .then(refresh)
        .then(() => {
          runCartRules();
          show(drawer, trigger);
        });
    }

    show(drawer, trigger);
    return syncCart();
  }

  function close() {
    const drawer = getDrawer();
    if (!drawer || !drawer.open || closeTimer) return;

    drawer.classList.remove(VISIBLE_CLASS);
    closeTimer = setTimeout(
      () => {
        closeTimer = null;
        drawer.close();
      },
      prefersReducedMotion() ? 0 : ANIMATION_DURATION
    );
  }

  function isOutside(drawer, event) {
    const rect = drawer.getBoundingClientRect();
    return (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom
    );
  }

  document.addEventListener(
    'click',
    (event) => {
      const openTrigger = event.target.closest(OPEN_TRIGGER_SELECTOR);
      if (openTrigger && getDrawer()) {
        event.preventDefault();
        event.stopPropagation();
        open(openTrigger);
        return;
      }

      const subscriptionSwitch = event.target.closest('[data-el-cart-subscription]');
      if (subscriptionSwitch && subscriptionSwitch.closest(DRAWER_SELECTOR)) {
        event.preventDefault();
        toggleSubscription(subscriptionSwitch);
        return;
      }

      const upsellButton = event.target.closest('[data-el-cart-upsell-add]');
      if (upsellButton && upsellButton.closest(DRAWER_SELECTOR)) {
        event.preventDefault();
        addUpsell(upsellButton);
        return;
      }

      const upsellDot = event.target.closest('[data-el-cart-upsell-dot]');
      if (upsellDot && upsellDot.closest(DRAWER_SELECTOR)) {
        event.preventDefault();
        goToUpsell(upsellDot);
        return;
      }

      const actionButton = event.target.closest('[data-el-cart-action]');
      if (actionButton && actionButton.closest(DRAWER_SELECTOR)) {
        event.preventDefault();
        handleAction(actionButton);
        return;
      }

      if (event.target.closest('[data-el-cart-close]')) {
        close();
        return;
      }

      const drawer = getDrawer();
      if (drawer && drawer.open && event.target === drawer && isOutside(drawer, event)) {
        close();
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form.matches || !form.matches('.el-cart-footer__form') || !form.closest(DRAWER_SELECTOR)) return;
      if (checkoutReady) {
        checkoutReady = false;
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      startCheckout(form, event.submitter || form.querySelector('[name="checkout"]'));
    },
    true
  );

  document.addEventListener(
    'cancel',
    (event) => {
      if (!event.target.matches || !event.target.matches(DRAWER_SELECTOR)) return;
      event.preventDefault();
      close();
    },
    true
  );

  document.addEventListener(
    'close',
    (event) => {
      if (!event.target.matches || !event.target.matches(DRAWER_SELECTOR)) return;
      document.documentElement.classList.remove(OPEN_CLASS);
      event.target.classList.remove(VISIBLE_CLASS);
      if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
      lastTrigger = null;
    },
    true
  );

  document.addEventListener(
    'scroll',
    (event) => {
      const target = event.target;
      if (target && target.matches && target.matches('[data-el-cart-upsell-track]')) updateUpsellDots(target);
    },
    true
  );

  window.addEventListener('resize', () => {
    const drawer = getDrawer();
    if (drawer && drawer.open) matchPageZoom(drawer);
  });

  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      syncTimer = null;
      if (isMutating()) {
        scheduleSync();
        return;
      }
      if (activeCartRequests === 0) syncCart();
    }, SYNC_DELAY);
  }

  function isOwnRequest(init) {
    const headers = init && init.headers;
    if (!headers) return false;
    if (typeof headers.get === 'function') return headers.get(OWN_REQUEST_HEADER) === '1';
    return headers[OWN_REQUEST_HEADER] === '1';
  }

  function trackCartRequest(request) {
    activeCartRequests += 1;
    const done = () => {
      activeCartRequests = Math.max(0, activeCartRequests - 1);
      scheduleSync();
    };
    return request.then(
      (result) => {
        done();
        return result;
      },
      (error) => {
        done();
        throw error;
      }
    );
  }

  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input && input.url ? input.url : String(input);
    const request = originalFetch.apply(this, arguments);
    if (CART_MUTATION_PATTERN.test(url) && !isOwnRequest(init)) return trackCartRequest(request);
    return request;
  };

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.elCartMutation = CART_MUTATION_PATTERN.test(String(url));
    return originalOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    if (this.elCartMutation) {
      activeCartRequests += 1;
      this.addEventListener('loadend', () => {
        activeCartRequests = Math.max(0, activeCartRequests - 1);
        scheduleSync();
      });
    }
    return originalSend.apply(this, arguments);
  };

  window.refreshCartDrawer = () => open(null, { waitForContent: true });
  window.onCartUpdated = () => open(null, { waitForContent: true });

  window.elCartDrawerV2 = { open, close, refresh };

  if (getDrawer()) {
    const initialBody = getDrawer().querySelector(BODY_SELECTOR);
    if (initialBody) writeCount(initialBody.dataset.elCartItemCount);
    watchCountElements();
    convertShippingProgress();
    watchDashedBorders();
    runCartRules();
    scheduleGiftCheck();
  }
})();
